import React, { useEffect, useState, useRef } from 'react';
import StaffScanGate from '@/components/StaffScanGate';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Scan, Loader2, Package, Check, ArrowRightLeft, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { pjoSplitText } from '@/components/processingJo/PjoOutcomePick';

const BOX_STATUS = { OPEN: 'Open', READY_FOR_INTERNAL_TRANSFER: 'Closed · waiting for Internal Transfer' };
const GONE = ['TRANSFERRED', 'TRANSFERRED_TO_DISPATCH'];

const FlexiAssortment = () => {
  const navigate = useNavigate();
  const [boxes, setBoxes] = useState([]);
  const [activeBox, setActiveBox] = useState('');
  const [boxInput, setBoxInput] = useState('');
  const [usn, setUsn] = useState('');
  const [scanning, setScanning] = useState(false);
  const [creatingBox, setCreatingBox] = useState(false);
  const usnRef = useRef(null);

  const loadBoxes = () =>
    apiClient.get('/flexi/assortment/boxes').then((r) =>
      // Boxes already on an Internal Transfer drop off the working list.
      setBoxes((r.data.boxes || []).filter((b) => !GONE.includes(b.status))),
    );

  useEffect(() => { loadBoxes(); }, []);
  useEffect(() => { if (activeBox && usnRef.current) usnRef.current.focus(); }, [activeBox]);

  const createBox = async () => {
    if (!boxInput.trim()) { toast.error('Scan the BUN bundle barcode for this box'); return; }
    setCreatingBox(true);
    try {
      const r = await apiClient.post('/flexi/assortment/boxes', { box_barcode: boxInput.trim() });
      toast.success(`Box ${r.data.box_barcode} opened`);
      setActiveBox(r.data.box_barcode);
      setBoxInput('');
      await loadBoxes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setCreatingBox(false); }
  };

  const scan = async (e) => {
    e?.preventDefault?.();
    const u = usn.trim();
    if (!u || !activeBox) return;
    setScanning(true);
    try {
      const r = await apiClient.post('/flexi/assortment/scan-usn', {
        box_barcode: activeBox, usn_code: u,
      });
      toast.success(`${u} boxed${r.data?.assembly_item ? ` — ${r.data.assembly_item}` : ''}${r.data?.pjo_id ? ` · job order ${r.data.pjo_id}` : ''}`);
      await loadBoxes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Scan failed'); }
    finally {
      // Always clear the input (even on a rejected scan) so a hardware scanner
      // can't concatenate the next code onto stale rejected text.
      setScanning(false); setUsn(''); setTimeout(() => usnRef.current?.focus(), 60);
    }
  };

  const closeBox = async (barcode) => {
    try {
      await apiClient.post(`/flexi/assortment/boxes/${barcode}/close`);
      toast.success(`Box ${barcode} closed — send it via Internal Transfer`);
      if (activeBox === barcode) setActiveBox('');
      await loadBoxes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const activeBoxDoc = boxes.find((b) => b.box_barcode === activeBox);

  return (
    <StaffScanGate stationName="Flexi — Assortment Packing">
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="flexi-assortment">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/flexi/hub')}
              className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Flexi — Assortment Packing</h1>
              <p className="text-sm text-[#E8DCC8]">Box-up received JW assemblies → close the box → send it via Internal Transfer</p>
            </div>
          </div>
          <Button variant="secondary" onClick={() => navigate('/sections/flexi/internal-transfer')}
            data-testid="go-internal-transfer-btn">
            <ArrowRightLeft className="w-4 h-4 mr-2" /> Internal Transfer
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Open a new box</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-2 items-end">
            <div>
              <Label className="text-xs">Box bundle barcode (scan the BUN sticker — one bundle = one box)</Label>
              <Input value={boxInput} onChange={(e) => setBoxInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && createBox()}
                className="w-[280px] mt-1" placeholder="BUN000001/26-27"
                data-testid="new-box-input" />
            </div>
            <Button onClick={createBox} disabled={creatingBox}
              className="bg-[#2D5F4F] hover:bg-[#234A3D]"
              data-testid="open-box-btn">
              {creatingBox ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
              <span className="ml-2">Open Box</span>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Boxes ({boxes.length})</CardTitle>
            <Button variant="ghost" size="sm" onClick={loadBoxes} data-testid="refresh-boxes-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Box</TableHead>
                  <TableHead>Assembly</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>USN Count</TableHead>
                  <TableHead>Job orders</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!boxes.length && (
                  <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-6">
                    No boxes yet — open one above and scan received USNs into it.
                  </TableCell></TableRow>
                )}
                {boxes.map((b) => (
                  <TableRow key={b.box_barcode} data-testid={`box-row-${b.box_barcode}`}>
                    <TableCell className="font-mono text-xs">{b.box_barcode}</TableCell>
                    <TableCell className="text-xs">
                      {(b.assembly_items && b.assembly_items.length)
                        ? b.assembly_items.join(', ')
                        : (b.assembly_item || '—')}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]" data-testid={`box-status-${b.box_barcode}`}>{BOX_STATUS[b.status] || b.status}</Badge></TableCell>
                    <TableCell>{b.usn_count}</TableCell>
                    <TableCell className="text-xs font-mono" data-testid={`box-pjo-${b.box_barcode}`}>{pjoSplitText(b.pjo_split) || '—'}</TableCell>
                    <TableCell className="text-right">
                      {b.status === 'OPEN' && (
                        <>
                          <Button size="sm" variant={activeBox === b.box_barcode ? 'default' : 'outline'}
                            onClick={() => setActiveBox(b.box_barcode)}
                            data-testid={`activate-box-${b.box_barcode}`}>
                            {activeBox === b.box_barcode ? 'Scanning' : 'Scan Into'}
                          </Button>
                          <Button size="sm" variant="ghost"
                            onClick={() => closeBox(b.box_barcode)}
                            title="Close box (ready for Internal Transfer)"
                            aria-label="Close box (ready for Internal Transfer)"
                            data-testid={`close-box-${b.box_barcode}`}>
                            <Check className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {activeBox && activeBoxDoc?.status === 'OPEN' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Scanning into {activeBox}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={scan} className="flex items-center gap-2">
                <Scan className="w-4 h-4 text-gray-500" />
                <Input ref={usnRef} value={usn}
                  onChange={(e) => setUsn(e.target.value)}
                  placeholder="Scan any received USN (from Job Order Receipt)…"
                  className="h-10 max-w-md" disabled={scanning}
                  data-testid="scan-usn-input" />
                <Button type="submit" disabled={scanning}
                  data-testid="scan-usn-submit">
                  {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Scan'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
    </StaffScanGate>
  );
};

export default FlexiAssortment;
